import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '../../common/Tooltip'
import Delete from '@mui/icons-material/Delete'
import PlayArrow from '@mui/icons-material/PlayArrow'
import Stop from '@mui/icons-material/Stop'

import { Input, InputAdminStatus, Role, User } from 'common/api/v1/types'
import { pluralize, useSettingsSelector, useUser } from '../../../utils'
import { Api, AppDispatch, GlobalState } from '../../../store'
import { DraftActions, draftInputs, DraftSources, enableInputs } from '../../../redux/actions/inputsActions'
import {
  ListInputFilterKey,
  makeListInputFilter,
  mapInputFilterToUrlParam,
  mapUrlParamToInputFilter,
} from './listInputFilter'
import { UrlParamFilteredSearchBar } from '../../common/Filters/FilterView/FilteredSearchBar'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'

interface CommonActionsProps {
  selected: Array<Input['id']>
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  user: User
  inputs: Input[]
}

const BatchEnableButton = ({ selected, setSelected }: Pick<CommonActionsProps, 'selected' | 'setSelected'>) => {
  const inputs = useSelector((state: GlobalState) => state.inputsReducer.inputs, shallowEqual)
  const dispatch = useDispatch<AppDispatch>()

  const selectedInputs = inputs.filter(({ id }) => selected.includes(id))
  const enable = selected.length > 0 && selectedInputs.some(({ adminStatus }) => adminStatus === InputAdminStatus.off)

  const onEnableClick = () => {
    dispatch(enableInputs(selected))
    dispatch(draftInputs({ inputs: [] }))
    setSelected([])
  }

  return (
    <Tooltip title={'Enable selected inputs'} disablePortal={false}>
      <span>
        <IconButton aria-label="Enable" disabled={!enable} onClick={onEnableClick} data-test-id="batch-enable">
          <PlayArrow />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const BatchDisableButton = ({ selected }: Pick<CommonActionsProps, 'selected'>) => {
  const inputs = useSelector((state: GlobalState) => state.inputsReducer.inputs, shallowEqual)
  const dispatch = useDispatch<AppDispatch>()

  const selectedInputs = inputs.filter(({ id }) => selected.includes(id))
  const enable = selected.length > 0

  const onDisableClick = () => {
    dispatch(draftInputs({ action: DraftActions.disable, inputs: selectedInputs, source: DraftSources.batch }))
  }

  return (
    <Tooltip title={'Disable selected inputs'} disablePortal={false}>
      <span>
        <IconButton aria-label="Disable" disabled={!enable} onClick={onDisableClick} data-test-id="batch-disable">
          <Stop />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const CommonActions = ({ inputs, selected, setSelected, user }: CommonActionsProps) => {
  const dispatch = useDispatch<AppDispatch>()

  const { group } = useUser()
  const inputsMap = inputs.reduce<{ [key: string]: Input }>((acc, item) => ({ ...acc, [item.id]: item }), {})

  const showButtons = user.role === Role.super || selected.every((id) => inputsMap[id]?.owner === group)

  const onDeleteClick = () =>
    dispatch(
      draftInputs({
        action: DraftActions.delete,
        inputs: selected.map((id) => inputsMap[id]),
        source: DraftSources.batch,
      }),
    )

  const { settings } = useSettingsSelector()
  const expFeatures = settings?.expFeatures
  const filters = makeListInputFilter({ api: Api, expFeatures })
  return (
    <Toolbar disableGutters style={{ overflowX: 'clip' }}>
      <Box
        sx={{
          transition: (theme: Theme) =>
            theme.transitions.create('flex-shrink', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
        }}
        style={{
          flexShrink: selected.length > 0 ? 1 : 0,
          flexGrow: 1,
          flexBasis: '100%',
        }}
      >
        <UrlParamFilteredSearchBar
          urlParamCacheKey={'inputs'}
          mapUrlParamToFilterFn={mapUrlParamToInputFilter}
          mapFilterToUrlParamFn={mapInputFilterToUrlParam}
          searchTokenFilters={filters}
          rawTextFilter={filters.find((f) => f.key === ListInputFilterKey.name)!}
        />
      </Box>

      <Box
        sx={{
          transition: (theme: Theme) =>
            // Fade out, i.e. keep visible until contents has left the screen
            theme.transitions.create('visibility', {
              delay: selected.length === 0 ? theme.transitions.duration.shortest : 0,
            }),
        }}
        style={{
          marginLeft: 16,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          visibility: selected.length > 0 ? 'visible' : 'hidden',
        }}
      >
        <div style={{ width: 140, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="subtitle1">{pluralize(selected.length, 'input')} selected</Typography>
        </div>

        {showButtons && (
          <div>
            <Tooltip title={'Delete selected inputs'} disablePortal={false}>
              <span>
                <IconButton aria-label="batch delete" data-test-id="batch-delete" onClick={onDeleteClick}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
            <BatchDisableButton selected={selected} />
            <BatchEnableButton selected={selected} setSelected={setSelected} />
          </div>
        )}
      </Box>
    </Toolbar>
  )
}

export default CommonActions
